import React, { useState } from 'react';
import { ArrowLeft, Package, Calendar, CheckCircle, AlertTriangle, Clock, ChevronLeft, ChevronRight, HelpCircle } from 'lucide-react';
import { Header } from '../components/Header';
import { BOMDetailsCard } from '../components/BOMDetailsCard';
import { AllergenSummaryCard } from '../components/AllergenSummaryCard';
import { ClaimsValidationCard } from '../components/ClaimsValidationCard';
import { BOMVersionHistory } from '../components/BOMVersionHistory';
import { CreateClaimModal } from '../components/CreateClaimModal';
type RiskLevel = 'none' | 'low' | 'medium' | 'high';
export function ProductDetail() {
  const [selectedBOMVersion, setSelectedBOMVersion] = useState('v2.1');
  const [activeTab, setActiveTab] = useState('claims'); // Default to 'claims'
  const [isCreateClaimModalOpen, setIsCreateClaimModalOpen] = useState(false);
  const [caseSkuPage, setCaseSkuPage] = useState(0);
  const product = {
    displayName: 'Advanced Multivitamin 60ct',
    sku: '0012345678901234',
    slug: 'A-MVM-60',
    brand: 'VitalSupps',
    manufacturer: 'NutraCorp Industries',
    listPrice: 29.99,
    category: 'Supplements',
    status: 'issues',
    lastUpdated: '2 hours ago',
    riskLevel: 'high' as RiskLevel,
    riskReason: 'Multiple unverified claims and allergen presence detected',
    image: 'https://images.unsplash.com/photo-1559181567-c3190ca9959b?w=120&h=120&fit=crop&crop=center',
    associatedCaseSkus: [{
      sku: '0112345678901234',
      displayName: 'Advanced Multivitamin 60ct - 12 Pack Case',
      itemQuantity: 12
    }, {
      sku: '0212345678901234',
      displayName: 'Advanced Multivitamin 60ct - 24 Pack Case',
      itemQuantity: 24
    }, {
      sku: '0312345678901234',
      displayName: 'Advanced Multivitamin 60ct - 36 Pack Case',
      itemQuantity: 36
    }, {
      sku: '0412345678901234',
      displayName: 'Advanced Multivitamin 60ct - 48 Pack Case',
      itemQuantity: 48
    }]
  };
  const CASES_PER_PAGE = 2;
  const totalPages = Math.ceil(product.associatedCaseSkus.length / CASES_PER_PAGE);
  const visibleCases = product.associatedCaseSkus.slice(caseSkuPage * CASES_PER_PAGE, (caseSkuPage + 1) * CASES_PER_PAGE);
  const nextPage = () => {
    if (caseSkuPage < totalPages - 1) {
      setCaseSkuPage(prev => prev + 1);
    }
  };
  const prevPage = () => {
    if (caseSkuPage > 0) {
      setCaseSkuPage(prev => prev - 1);
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'validated':
        return <CheckCircle className="w-5 h-5 text-green-600" data-id="element-262" />;
      case 'issues':
        return <AlertTriangle className="w-5 h-5 text-red-600" data-id="element-263" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-yellow-600" data-id="element-264" />;
      default:
        return null;
    }
  };
  const getStatusText = (status: string) => {
    switch (status) {
      case 'validated':
        return 'All Claims Validated';
      case 'issues':
        return 'Issues Detected';
      case 'pending':
        return 'Pending Review';
      default:
        return status;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'validated':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'issues':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };
  const getRiskColor = (level: RiskLevel) => {
    switch (level) {
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      case 'medium':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'low':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'none':
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };
  const getRiskLabel = (score: number) => {
    if (score >= 70) return 'High Risk';
    if (score >= 30) return 'Medium Risk';
    return 'Low Risk';
  };
  const renderTabContent = () => {
    if (activeTab === 'bom') {
      return <div className="grid grid-cols-1 lg:grid-cols-4 gap-6" data-id="element-265">
          <div className="lg:col-span-3 space-y-6" data-id="element-266">
            <div className="bg-white rounded-lg border" data-id="element-267">
              <div className="p-6 border-b border-gray-200" data-id="element-268">
                <div className="flex items-center space-x-2" data-id="element-269">
                  <Package className="w-5 h-5 text-gray-600" data-id="element-270" />
                  <div data-id="element-271">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-272">
                      Bill of Materials (BOM)
                    </h2>
                    <p className="text-sm text-gray-600" data-id="element-273">
                      Imported case packaging and contents details
                    </p>
                  </div>
                </div>
              </div>
              <BOMDetailsCard data-id="element-274" />
            </div>
          </div>
          <div className="space-y-6" data-id="element-275">
            <BOMVersionHistory selectedVersion={selectedBOMVersion} data-id="element-276" />
          </div>
        </div>;
    }
    return <div className="space-y-6" data-id="element-277">
        <ClaimsValidationCard onAddClaim={() => setIsCreateClaimModalOpen(true)} data-id="element-278" />
        <AllergenSummaryCard data-id="element-279" />
        <CreateClaimModal isOpen={isCreateClaimModalOpen} onClose={() => setIsCreateClaimModalOpen(false)} productSku={product.sku} productName={product.displayName} data-id="element-280" />
      </div>;
  };
  return <div className="w-full min-h-screen bg-gray-50" data-id="element-281">
      <Header data-id="element-282" />
      <div className="bg-white border-b border-gray-200" data-id="element-283">
        <div className="max-w-7xl mx-auto px-6 py-6" data-id="element-284">
          {/* Breadcrumb Navigation */}
          <nav className="flex items-center space-x-2 text-sm mb-6" data-id="element-285">
            <a href="#" className="text-gray-600 hover:text-gray-900" data-id="element-286">
              Products
            </a>
            <span className="text-gray-400" data-id="element-287">/</span>
            <span className="text-gray-900 font-medium" data-id="element-288">{product.sku}</span>
            <span className="text-gray-400" data-id="element-289">/</span>
            <span className="text-gray-600" data-id="element-290">{product.displayName}</span>
          </nav>
          <div className="flex items-start justify-between" data-id="element-291">
            <div className="flex items-start space-x-4" data-id="element-292">
              <img src={product.image} alt={product.displayName} className="w-16 h-16 rounded-lg object-cover" data-id="element-293" />
              <div data-id="element-294">
                <h1 className="text-2xl font-bold text-gray-900" data-id="element-295">
                  {product.displayName}
                </h1>
                <div className="flex flex-col space-y-2 mt-2" data-id="element-296">
                  <div className="flex items-center space-x-4" data-id="element-297">
                    <span className="text-sm font-medium text-gray-700" data-id="element-298">
                      SKU:
                    </span>
                    <span className="text-sm text-gray-900" data-id="element-299">{product.sku}</span>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-300">
                    <span className="text-sm font-medium text-gray-700" data-id="element-301">
                      Manufacturer:
                    </span>
                    <span className="text-sm text-gray-900" data-id="element-302">
                      {product.manufacturer}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-303">
                    <span className="text-sm font-medium text-gray-700" data-id="element-304">
                      List Price:
                    </span>
                    <span className="text-sm text-gray-900" data-id="element-305">
                      ${product.listPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            {/* Risk Score */}
            <div className="flex flex-col items-end space-y-4" data-id="element-306">
              <div className="relative" data-id="element-307">
                <div className={`flex flex-col items-center p-4 rounded-lg border ${getRiskColor(product.riskLevel)}`} data-id="element-308">
                  <div className="text-lg font-bold capitalize mb-1" data-id="element-309">
                    {product.riskLevel} Risk
                  </div>
                  <button className="absolute top-2 right-2 text-gray-500 hover:text-gray-700" title={product.riskReason} data-id="element-310">
                    <HelpCircle className="w-4 h-4" data-id="element-311" />
                  </button>
                </div>
              </div>
              <div className="flex items-center space-x-2" data-id="element-312">
                <label className="text-sm font-medium text-gray-700" data-id="element-313">
                  BOM Version:
                </label>
                <select value={selectedBOMVersion} onChange={e => setSelectedBOMVersion(e.target.value)} className="px-3 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500" data-id="element-314">
                  <option value="v2.1" data-id="element-315">v2.1 (Active)</option>
                  <option value="v2.0" data-id="element-316">v2.0</option>
                  <option value="v1.3" data-id="element-317">v1.3</option>
                </select>
              </div>
              <div className={`flex items-center space-x-2 px-3 py-2 rounded-lg border ${getStatusColor(product.status)}`} data-id="element-318">
                {getStatusIcon(product.status)}
                <span className="font-medium" data-id="element-319">
                  {getStatusText(product.status)}
                </span>
              </div>
            </div>
          </div>
          {/* Associated Case SKUs */}
          <div className="mt-6 border-t border-gray-200 pt-6" data-id="element-320">
            <div className="flex items-center justify-between mb-3" data-id="element-321">
              <h2 className="text-sm font-medium text-gray-700" data-id="element-322">
                Associated Case Configurations
              </h2>
              <span className="text-sm text-gray-500" data-id="element-323">
                {product.associatedCaseSkus.length} configurations
              </span>
            </div>
            <div className="relative" data-id="element-324">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-325">
                {visibleCases.map(caseSku => <div key={caseSku.sku} className="border border-gray-200 rounded-lg p-4 hover:border-purple-200 hover:bg-purple-50 transition-colors cursor-pointer" data-id="element-326">
                    <div className="flex flex-col space-y-2" data-id="element-327">
                      <span className="font-medium text-gray-900" data-id="element-328">
                        {caseSku.displayName}
                      </span>
                      <div className="flex items-center space-x-2" data-id="element-329">
                        <span className="text-sm text-gray-600" data-id="element-330">SKU:</span>
                        <span className="text-sm text-gray-900" data-id="element-331">
                          {caseSku.sku}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2" data-id="element-332">
                        <span className="text-sm text-purple-600 font-medium" data-id="element-333">
                          Contains {caseSku.itemQuantity} Item Units
                        </span>
                      </div>
                    </div>
                  </div>)}
              </div>
              {caseSkuPage > 0 && <button onClick={prevPage} className="absolute left-0 top-1/2 -translate-y-1/2 -ml-4 p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50" data-id="element-334">
                  <ChevronLeft className="w-5 h-5 text-gray-600" data-id="element-335" />
                </button>}
              {caseSkuPage < totalPages - 1 && <button onClick={nextPage} className="absolute right-0 top-1/2 -translate-y-1/2 -mr-4 p-1 rounded-full bg-white border border-gray-200 shadow-sm hover:bg-gray-50" data-id="element-336">
                  <ChevronRight className="w-5 h-5 text-gray-600" data-id="element-337" />
                </button>}
            </div>
          </div>
          <div className="flex items-center space-x-2 mt-4 text-sm text-gray-500" data-id="element-338">
            <Calendar className="w-4 h-4" data-id="element-339" />
            <span data-id="element-340">Last updated {product.lastUpdated}</span>
          </div>
        </div>
      </div>
      {/* Tabs */}
      <div className="border-b border-gray-200" data-id="element-341">
        <div className="max-w-7xl mx-auto px-6" data-id="element-342">
          <nav className="-mb-px flex space-x-8" data-id="element-343">
            <button onClick={() => setActiveTab('claims')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'claims' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-344">
              Claims & Allergens
            </button>
            <button onClick={() => setActiveTab('bom')} className={`py-4 px-1 border-b-2 font-medium text-sm ${activeTab === 'bom' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-345">
              Bill of Materials
            </button>
          </nav>
        </div>
      </div>
      <main className="max-w-7xl mx-auto px-6 py-6" data-id="element-346">{renderTabContent()}</main>
    </div>;
}